// Scherm 10 — het trend-detailpaneel. Blokken en vorm exact als de dummy
// (V1 r. 54-79): kop, tldr in een grijs kader, narratief met bronpillen op de
// {n}-markers, Scores, Impact op onderwijs, Brainport-regio, Afgeleide skills,
// Bronnen als tijdlijn per kwartaal.
//
// Wat W8 hier bindend maakt:
//  · dekkende kop tonen, bevroren titel als terugval (regel 4)
//  · elke koppeling toont zijn bewijstrede; op de zwakke trede geen causale
//    taal — "raakt aan" in plaats van "vraagt om" (regel 3)
//  · het Brainport-blok draagt zichtbaar "interpretatie, geen regionale bron"
//    zolang er geen regionaal bewijs onder ligt (W8 §3, voorwaarde bij A3b)
//  · een leeg veld zegt waarom het leeg is (regel 1)

import { Fragment, useEffect, useRef, useState, type CSSProperties } from "react";

import type { Trend } from "../contracts";
import { AFGELEID_UITLEG, TREDE_LABEL, TREDE_UITLEG } from "../lib/schermen";
// Kleuren via de chattokens: in de oude weergave dezelfde waarden, in de
// nieuwe volgen ze licht en donker (testfeedback 03-11, rij 6).
import { color, radius, size, weight } from "./chatTokens";
import { BetaalmuurPil, Bewijsbalk, BewijsBadge, Bronregel, heeftBewijsvelden, isGezagBadge } from "./Bewijs";
import { ComingSoon, EmptyReason } from "./Empty";
import { TrendRegelingen } from "./RegelingenStrook";
import { PanelHeader, ReasonBadge, SectionLabel } from "./ui";

/** Een cijfer met één decimaal en een komma, zoals in het Nederlands (testfeedback 03-11, rij 6). */
const komma = (n: number) => n.toFixed(1).replace(".", ",");

type Evidence = Trend["evidence"][number];

type ScoreKey = "trending" | "impact" | "urgency";

// Kleuren letterlijk uit de dummy: trending blauw, impact groen, urgentie amber.
const SCORE_CARDS: { key: ScoreKey; label: string; color: string }[] = [
  { key: "trending", label: "Trending", color: color.accentBlue },
  { key: "impact", label: "Impact", color: color.accentGreen },
  { key: "urgency", label: "Urgentie", color: color.accentAmber },
];

const relRow: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  width: "100%",
  textAlign: "left",
  background: color.surfaceQuote,
  border: `1px solid ${color.line}`,
  borderRadius: radius.quote,
  padding: "8px 10px",
  fontSize: size.meta,
  fontFamily: "inherit",
  color: color.body,
  cursor: "pointer",
};

// K4 (bouwrun R1): ook "zelfde bron, afgeleid" — één woordenlijst in lib/schermen.
const RUNG_LABEL: Record<string, string> = TREDE_LABEL;

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return "datum onbekend";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "datum onbekend";
  return d.toLocaleDateString("nl-NL", { month: "short", year: "numeric" });
}

function quarter(iso: string | null | undefined): string {
  if (!iso) return "zonder datum";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "zonder datum";
  return `Q${Math.ceil((d.getMonth() + 1) / 3)} ${d.getFullYear()}`;
}

/** De bronpil die in het narratief op de plek van een {n}-marker staat. */
function SourcePill({
  evidence,
  active,
  onHover,
  onLeave,
  onClick,
}: {
  evidence: Evidence;
  active: boolean;
  onHover: () => void;
  onLeave: () => void;
  onClick: () => void;
}) {
  const label =
    evidence.publisher.length > 25 ? `${evidence.publisher.slice(0, 23)}..` : evidence.publisher;
  return (
    <span
      onClick={onClick}
      onMouseEnter={onHover}
      onMouseLeave={onLeave}
      // Zonder gezagscijfer (methode v3): wie het zegt staat in de bronregel
      // onderaan, met waarop en hoeveel het telt.
      title={`${evidence.publisher} — ${fmtDate(evidence.date)}`}
      style={{
        display: "inline",
        fontSize: 11,
        fontWeight: weight.regular,
        color: active ? color.ink : color.faint,
        background: active ? color.lineSoft : color.surfaceQuote,
        border: `1px solid ${active ? color.disabled : color.lineSoft}`,
        padding: "1px 7px",
        borderRadius: 4,
        cursor: "pointer",
        transition: "all 0.12s",
        whiteSpace: "nowrap",
        verticalAlign: "baseline",
        marginLeft: 2,
        marginRight: 1,
      }}
    >
      {label}
    </span>
  );
}

export function TrendDetail({
  trend,
  onClose,
  onOpenTrend,
  childTrends = [],
  parent = null,
}: {
  trend: Trend;
  onClose: () => void;
  onOpenTrend?: (t: Trend) => void;
  /** De trends die deze trend onderbouwen (de lijst kent ze al). */
  childTrends?: Trend[];
  /** De trend waar deze onder hangt, als die op dezelfde pagina staat. */
  parent?: Trend | null;
}) {
  const [highlighted, setHighlighted] = useState<string | null>(null);
  const [openScore, setOpenScore] = useState<ScoreKey | null>(null);
  const scroller = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scroller.current) scroller.current.scrollTop = 0;
  }, [trend.id]);

  // Het markercontract: {n} is een 0-based index in de OP DATUM GESORTEERDE
  // bewijslijst (code-bewaakt aan de schrijfkant, W14). Hier sorteren we dus op
  // dezelfde manier, precies zoals de dummy dat deed.
  const evidence = [...trend.evidence].sort((a, b) => {
    const da = a.date ? Date.parse(a.date) : Number.POSITIVE_INFINITY;
    const db = b.date ? Date.parse(b.date) : Number.POSITIVE_INFINITY;
    return da - db;
  });

  const scrollToSource = (id: string) => {
    setHighlighted(id);
    document.getElementById(`src-${id}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  // Het markercontract geldt NIET alleen voor het narratief: gemeten op de
  // huidige generatie dragen ook 501 onderwijs-nu-teksten, 900 onderwijs-straks-
  // teksten en 195 motiveringen een {n}. Eén renderer dus, voor elk tekstveld —
  // anders staat er letterlijk "{4}" in het scherm.
  const renderText = (text: string) =>
    text.split(/(\{\d+\})/g).map((segment, i) => {
      const marker = segment.match(/^\{(\d+)\}$/);
      if (marker) {
        const item = evidence[Number(marker[1])];
        if (!item) return null; // marker buiten bereik: liever niets dan een verkeerde bron
        return (
          <SourcePill
            key={`m${i}`}
            evidence={item}
            active={highlighted === item.id}
            onHover={() => setHighlighted(item.id)}
            onLeave={() => setHighlighted(null)}
            onClick={() => scrollToSource(item.id)}
          />
        );
      }
      if (!segment) return null;
      return segment.split("\n\n").map((paragraph, li) => (
        <Fragment key={`s${i}_${li}`}>
          {li > 0 && <span style={{ display: "block", height: 10 }} />}
          {paragraph}
        </Fragment>
      ));
    });

  const byQuarter = new Map<string, Evidence[]>();
  for (const item of evidence) {
    const q = quarter(item.date);
    byQuarter.set(q, [...(byQuarter.get(q) ?? []), item]);
  }

  const divider = <div style={{ height: 1, background: color.lineFaint, marginBottom: 20 }} />;

  return (
    <div ref={scroller} style={{ height: "100%", overflowY: "auto" }}>
      <PanelHeader onClose={onClose}>
        <ReasonBadge text={trend.level} />
        {trend.laag && (
          <ReasonBadge
            text={trend.laag === "drager" ? "dragende trend" : "onderbouwing"}
            title={
              trend.laag === "drager"
                ? "Deze trend staat op eigen benen in de lijst."
                : "Deze trend onderbouwt een andere trend en staat daar als bewijsregel onder."
            }
          />
        )}
        {trend.category && <ReasonBadge text={trend.category} />}
        {trend.categoryProposed && (
          <ReasonBadge
            text={`voorstel: ${trend.categoryProposed}`}
            tone="amber"
            title={
              "Deze categorie is voorgesteld door de leeslaag maar nog niet vastgesteld: " +
              "hij haalt de promotiedrempel (vijf onafhankelijke uitgevers) nog niet."
            }
          />
        )}
        {/* Methode v3: is er bewijs, dan zegt de badge WIE de trend bevestigt
            ("bevestigd door: 2 instituten + 1 vakblad") en vervangt hij de
            gezag-gebaseerde uitlichtbadge ("zwaar rapport (autoriteit 9,0)").
            Zonder bewijs blijven de uitlichtbadges staan zoals ze waren. */}
        {(trend.uitlichtBadges ?? [])
          .filter((badge) => !(trend.bewijs && (isGezagBadge(badge) || badge === trend.bewijs.tekst)))
          .map((badge) => (
            <ReasonBadge key={badge} text={badge} tone="blue" />
          ))}
        {trend.bewijs && (
          <>
            <BewijsBadge bewijs={trend.bewijs} />
            <Bewijsbalk bewijs={trend.bewijs} />
          </>
        )}
      </PanelHeader>

      <div style={{ padding: 24 }}>
        <h2
          style={{
            fontSize: size.panelTitle,
            fontWeight: weight.bold,
            color: color.ink,
            lineHeight: 1.3,
            letterSpacing: "-0.02em",
            marginBottom: 10,
          }}
        >
          {trend.displayTitle || trend.title}
        </h2>
        {trend.displayTitle && trend.displayTitle !== trend.title && (
          <p style={{ fontSize: 12, color: color.faint, marginBottom: 10 }}>
            vastgelegde titel: {trend.title}
          </p>
        )}

        <div
          style={{
            background: color.surfaceQuote,
            borderRadius: radius.quote,
            padding: "10px 14px",
            marginBottom: 16,
          }}
        >
          <p
            style={{
              fontSize: size.body,
              color: color.body,
              lineHeight: 1.55,
              fontStyle: "italic",
            }}
          >
            {trend.tldr}
          </p>
        </div>

        <p style={{ fontSize: size.body, color: color.muted, lineHeight: 1.75, marginBottom: 20 }}>
          {renderText(trend.description)}
        </p>

        {divider}

        <div style={{ marginBottom: 20 }}>
          <SectionLabel>Scores</SectionLabel>
          {/* Exact de vorm van de dummy (V1 r. 68): drie kaartjes naast elkaar,
              label klein, het cijfer groot (26px/700), daaronder een gekleurd
              balkje. Klikken op een kaartje vouwt de motivering eronder open —
              in de dummy stond die er alleen als scoreR bestond; bij ons draagt
              élke trend een motivering, en die mag markers bevatten. */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
            {SCORE_CARDS.map((card) => {
              const value = trend.scores?.[card.key];
              const open = openScore === card.key;
              const rationale = trend.scoreRationale?.[card.key];
              return (
                <div
                  key={card.key}
                  onClick={() => setOpenScore(open ? null : card.key)}
                  title={rationale ? "Klik voor de motivering" : undefined}
                  style={{
                    background: color.surfaceQuote,
                    border: `1px solid ${open ? color.disabled : "transparent"}`,
                    borderRadius: radius.block,
                    padding: 12,
                    cursor: rationale ? "pointer" : "default",
                    transition: "border-color 0.15s",
                  }}
                >
                  <div
                    style={{
                      fontSize: 10,
                      color: color.faint,
                      fontWeight: weight.medium,
                      letterSpacing: "0.06em",
                      textTransform: "uppercase",
                      marginBottom: 6,
                    }}
                  >
                    {card.label}
                  </div>
                  <div
                    style={{
                      fontSize: 26,
                      fontWeight: weight.bold,
                      color: color.ink,
                      lineHeight: 1,
                      marginBottom: 6,
                    }}
                  >
                    {typeof value === "number" ? komma(value) : "—"}
                  </div>
                  <div style={{ height: 3, background: color.border, borderRadius: 99 }}>
                    <div
                      style={{
                        height: "100%",
                        width: `${typeof value === "number" ? Math.max(0, Math.min(10, value)) * 10 : 0}%`,
                        background: card.color,
                        borderRadius: 99,
                      }}
                    />
                  </div>
                  {open && rationale && (
                    <div
                      style={{
                        marginTop: 8,
                        fontSize: 12,
                        color: color.soft,
                        lineHeight: 1.6,
                        borderTop: `1px solid ${color.border}`,
                        paddingTop: 6,
                      }}
                    >
                      {renderText(rationale)}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {divider}

        <div style={{ marginBottom: 20 }}>
          <SectionLabel>Impact op onderwijs</SectionLabel>
          {/* E3 (bouwrun R1): deze twee teksten schreef het platform, geen
              bron. Etiket en een gestippelde rand in plaats van de doorgetrokken
              citaatlijn; zonder veld (oude API) blijft het scherm zoals het was. */}
          {trend.education?.herkomst === "afgeleid" && (
            <div data-testid="onderwijs-afgeleid" style={{ marginBottom: 8 }}>
              <ReasonBadge
                text={trend.education.herkomstLabel || "afgeleid door het platform"}
                tone="amber"
                title={AFGELEID_UITLEG}
              />
            </div>
          )}
          <div
            style={{
              background: color.surfaceQuote,
              borderRadius: radius.block,
              padding: 14,
              borderLeft:
                trend.education?.herkomst === "afgeleid"
                  ? `3px dashed ${color.disabled}`
                  : `3px solid ${color.ink}`,
              marginBottom: 10,
            }}
          >
            <div
              style={{
                fontSize: 11,
                fontWeight: weight.medium,
                color: color.faint,
                textTransform: "uppercase",
                letterSpacing: "0.08em",
                marginBottom: 6,
              }}
            >
              Directe impact
            </div>
            <p style={{ fontSize: size.body, color: color.body, lineHeight: 1.6 }}>
              {trend.education?.now ? (
                renderText(trend.education.now)
              ) : (
                <EmptyReason reason="geen directe impact beschreven" />
              )}
            </p>
          </div>
          <div
            style={{
              background: color.surfaceQuote,
              borderRadius: radius.block,
              padding: 14,
              borderLeft: `3px ${trend.education?.herkomst === "afgeleid" ? "dashed" : "solid"} ${color.disabled}`,
            }}
          >
            <div
              style={{
                fontSize: 11,
                fontWeight: weight.medium,
                color: color.faint,
                textTransform: "uppercase",
                letterSpacing: "0.08em",
                marginBottom: 6,
              }}
            >
              Gevolg voor later
            </div>
            <p style={{ fontSize: size.body, color: color.body, lineHeight: 1.6 }}>
              {trend.education?.future ? (
                renderText(trend.education.future)
              ) : (
                <EmptyReason reason="geen gevolg voor later beschreven" />
              )}
            </p>
          </div>
        </div>

        {trend.brainport ? (
          <>
            {divider}
            <div style={{ marginBottom: 20 }}>
              <SectionLabel>Brainport-regio</SectionLabel>
              <div
                style={{
                  background: color.accentAmberBg,
                  borderRadius: radius.block,
                  padding: 14,
                  borderLeft: `3px solid ${color.accentAmber}`,
                }}
              >
                <div style={{ marginBottom: 8 }}>
                  <ReasonBadge
                    tone="amber"
                    text={
                      (trend.brainport.sources ?? []).length > 0
                        ? "regionale bron aanwezig"
                        : "interpretatie, geen regionale bron"
                    }
                    title={
                      (trend.brainport.sources ?? []).length > 0
                        ? "Onder deze alinea ligt bewijs van een regionale uitgever."
                        : "Deze alinea is een interpretatie van landelijk en Europees bewijs. " +
                          "Er is geen regionale bron die dit voor Brainport meet."
                    }
                  />
                </div>
                <p style={{ fontSize: size.body, color: color.body, lineHeight: 1.6 }}>
                  {trend.brainport.comparison}
                </p>
                <p
                  style={{
                    fontSize: size.meta,
                    color: color.soft,
                    lineHeight: 1.6,
                    marginTop: 8,
                  }}
                >
                  {trend.brainport.context}
                </p>
              </div>
            </div>
          </>
        ) : null}

        {divider}

        <div style={{ marginBottom: 20 }}>
          <SectionLabel>Afgeleide skills ({trend.skills.length})</SectionLabel>
          {trend.skills.length === 0 ? (
            <EmptyReason reason="Nog geen vaardigheid gekoppeld: de brug vond onder deze trend geen bewijs dat een skill aanwijst." />
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              {trend.skills.map((skill) => {
                const weak = skill.relation === "raakt";
                return (
                  <div
                    key={skill.id}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      padding: "8px 10px",
                      background: color.surfaceQuote,
                      borderRadius: radius.quote,
                    }}
                  >
                    <span
                      style={{ fontSize: size.meta, color: color.body, flex: 1, minWidth: 0 }}
                    >
                      {skill.name}
                    </span>
                    {/* Regel 3: op de zwakke trede geen causale taal. */}
                    <span style={{ fontSize: 11, color: color.faint, whiteSpace: "nowrap" }}>
                      {weak ? "raakt aan" : "vraagt om"}
                    </span>
                    {skill.evidenceRung === "zelfde_bron_afgeleid" ? (
                      <ReasonBadge
                        text={TREDE_LABEL.zelfde_bron_afgeleid}
                        tone="neutral"
                        title={TREDE_UITLEG.zelfde_bron_afgeleid}
                      />
                    ) : skill.evidenceRung && (
                      <ReasonBadge
                        text={weak ? "zwakkere grond" : "harde grond"}
                        tone={weak ? "neutral" : "blue"}
                        title={`Bewijstrede: ${RUNG_LABEL[skill.evidenceRung] ?? skill.evidenceRung}`}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {(parent || childTrends.length > 0 || trend.childIds.length > 0) && (
          <>
            {divider}
            <div style={{ marginBottom: 20 }}>
              <SectionLabel>Structuur</SectionLabel>
              {parent && (
                <div style={{ marginBottom: 10 }}>
                  <div style={{ fontSize: 11, color: color.faint, marginBottom: 4 }}>
                    hoort onder
                  </div>
                  <button onClick={() => onOpenTrend?.(parent)} style={relRow}>
                    <span style={{ color: color.faint, flexShrink: 0 }}>&uarr;</span>
                    <span style={{ flex: 1, minWidth: 0 }}>
                      {parent.displayTitle || parent.title}
                    </span>
                  </button>
                </div>
              )}
              {childTrends.length > 0 ? (
                <>
                  <div style={{ fontSize: 11, color: color.faint, marginBottom: 4 }}>
                    onderbouwd door ({childTrends.length})
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    {childTrends.map((child) => (
                      <button
                        key={child.id}
                        onClick={() => onOpenTrend?.(child)}
                        style={relRow}
                      >
                        <span style={{ color: color.faint, flexShrink: 0 }}>&#8627;</span>
                        <span style={{ flex: 1, minWidth: 0 }}>
                          {child.displayTitle || child.title}
                        </span>
                        <span style={{ fontSize: 11, color: color.faint, flexShrink: 0 }}>
                          {child.sourceCount} {child.sourceCount === 1 ? "bron" : "bronnen"}
                        </span>
                      </button>
                    ))}
                  </div>
                </>
              ) : trend.childIds.length > 0 ? (
                <EmptyReason
                  reason={
                    `${trend.childIds.length} onderliggende trends — open deze trend ` +
                    "vanuit de lijst om ze te zien."
                  }
                />
              ) : null}
            </div>
          </>
        )}

        {divider}

        <div style={{ marginBottom: 20 }}>
          <SectionLabel>Bronnen ({evidence.length})</SectionLabel>
          <div style={{ position: "relative", paddingLeft: 18 }}>
            <div
              style={{
                position: "absolute",
                left: 5,
                top: 4,
                bottom: 4,
                width: 1.5,
                background: color.lineFaint,
              }}
            />
            {[...byQuarter.entries()].map(([q, items]) => (
              <div key={q} style={{ marginBottom: 14 }}>
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: weight.medium,
                    color: color.faint,
                    marginBottom: 6,
                  }}
                >
                  {q}
                </div>
                {items.map((item) => (
                  <div
                    key={item.id}
                    id={`src-${item.id}`}
                    style={{
                      background:
                        highlighted === item.id ? color.surfaceAlt : color.surface,
                      border: `1px solid ${
                        highlighted === item.id ? color.disabled : color.lineFaint
                      }`,
                      borderRadius: radius.quote,
                      padding: "10px 12px",
                      marginBottom: 6,
                      transition: "all 0.15s",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "baseline",
                        gap: 8,
                        marginBottom: 4,
                        flexWrap: "wrap",
                      }}
                    >
                      <span
                        style={{
                          fontSize: 12,
                          fontWeight: weight.medium,
                          color: color.ink,
                        }}
                      >
                        {item.publisher}
                      </span>
                      <span style={{ fontSize: 11, color: color.faint }}>
                        {item.date ? fmtDate(item.date) : "datum onbekend"}
                      </span>
                      {/* Methode v3: wie · waarop · punten in plaats van het
                          gezagscijfer. Ontbreken de velden, dan staat het
                          cijfer er zoals vóór deze wijziging. */}
                      {heeftBewijsvelden(item) ? (
                        <Bronregel bron={item} />
                      ) : (
                        <span
                          style={{ fontSize: 11, color: color.faint }}
                          title="Gezagscijfer van de uitgever, 0-10 (rubriekdimensie source_credibility)."
                        >
                          gezag {item.authority}
                        </span>
                      )}
                      {!heeftBewijsvelden(item) && <BetaalmuurPil bron={item} />}
                      {item.url && (
                        <a
                          href={item.url}
                          target="_blank"
                          rel="noreferrer"
                          style={{ fontSize: 11, color: color.accentBlue }}
                        >
                          bron openen
                        </a>
                      )}
                    </div>
                    <p style={{ fontSize: 12, color: color.muted, lineHeight: 1.55 }}>
                      “{item.quote}”
                    </p>
                    <p style={{ fontSize: 11, color: color.faint, marginTop: 4 }}>{item.title}</p>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>

        {/* Funding fase 5: de regelingenstrook op de sector van de trend. */}
        <TrendRegelingen sectors={trend.sectors} />

        <ComingSoon
          title="Beschikbare scholing"
          reason={
            "Om te tonen wat er al aan opleiding tegenover staat, is een lijst van het " +
            "onderwijsaanbod nodig. Die bestaat nog niet; hij hangt aan de crebo-koppeling."
          }
        />
      </div>
    </div>
  );
}
